import { useState, useRef } from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { deleteCookie, getCookie, getLocalStorage, setCookie, setLocalStorage } from './aaa';
import { getStore } from './ApplicationState';
import { useAuth } from './CustomHooks';
import { canShouldUserAutoSync } from './GlobalFunctions';
import SpinnerOverlay from './SpinnerOverlay';
import { isElementaryStudent, isStudent } from './UserManagementHelpers';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { decideShouldPromptForSurvey } from './SurveyPromptDecisionService';
import Analytics from './Analytics';
import { useErrorStatus } from './components/EnableErrorMessagePopup/EnableErrorMessagePopup';
import { isLikelyNetworkTimeoutError } from './UserManagementHelpers';

interface Props {
  children: JSX.Element | JSX.Element[];
  requiredKeys?: string | string[];
  [key: string]: unknown;
}

export const AuthenticatedRoute = ({ children, requiredKeys, ...rest }: Props) => {
  const location = useLocation();
  // console.debug(
  //   `%cAuthenticated route render `,
  //   'background: blue; color: yellow',
  //   location.pathname
  // );
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(false);
  const auth = useAuth();
  const onShowErrorMessage = useErrorStatus();
  const surveyRedirectCount = useRef(0);
  const lastSurveyRedirectPath = useRef<string | null>(null);

  if (auth.isUserLoggedInStateUnknown()) {
    //console.debug( `%cRoot login state unknown, attempting to reactivate`, 'background: #222; color: #bada55' )
    void auth.reactivateUserSession().catch((error: unknown) => {
      setIsCheckingAuth(false);

      if (isLikelyNetworkTimeoutError(error) && typeof onShowErrorMessage === 'function') {
        onShowErrorMessage(
          'We are having trouble reaching Peekapak right now. Please check your connection and try again.',
          'Connection timed out',
        );
        return;
      }

      console.error('Cannot reactivate user session from AuthenticatedRoute:', error);
    });
    setIsCheckingAuth(true);
    return <SpinnerOverlay isShow={true} />;
  }

  if (auth.isUserLoggedInStateInTransition()) {
    auth.onLoggedInStateKnown(() => {
      setIsCheckingAuth(false);
    });
    return <SpinnerOverlay isShow={true} />;
  }

  if (isCheckingAuth) {
    return <SpinnerOverlay isShow={true} />;
  }

  if (auth.isUserSessionExpired()) {
    return (
      <Route
        render={({ location }) => (
          <Redirect
            to={{
              pathname: '/sessionExpired',
              state: { nextPathname: location.pathname },
            }}
          />
        )}
      />
    );
  }

  if (!auth.isUserLoggedIn() && !auth.isUserParentAccessStorybook()) {
    // doesn't seem like the user is logged in or using story book access, but let's check just to be sure

    // was this a parent story book access link?
    // if so, we need to redirect to the storybook
    const storybookAccessUri = getCookie('peekapak.storybookForParents');
    if (storybookAccessUri) {
      window.location.href = storybookAccessUri;
      return;
    }

    if (rest.location instanceof Location) {
      setCookie('peekapak.pathBeforeLoginRedirect', rest.location.pathname, 7);
    }

    return (
      <Route
        render={({ location }) => (
          <Redirect
            to={{
              pathname: '/membersOnlyRedirect',
              state: { nextPathname: location.pathname },
            }}
          />
        )}
      />
    );
  }

  const keysAsArray = requiredKeys ? (!Array.isArray(requiredKeys) ? [requiredKeys] : requiredKeys) : [];

  for (let i = 0; i < keysAsArray.length; i += 1) {
    if (!auth.isUserHasKey(keysAsArray[i])) {
      return <Route {...rest} render={(_) => <Redirect to={{ pathname: '/unauthorized' }} />} />;
    }
  }

  return performPreUseOperations();

  function performPreUseOperations() {
    const { userProfile } = getStore().getState().user;

    if (auth.isUserMissingSchool() && !isSkipCheckRouteForMissingSchool() && !isStudent(userProfile)) {
      return (
        <Route
          render={({ location }) => (
            <Redirect
              to={{
                pathname: '/moreInformation/school',
                state: { nextPathname: location.pathname },
              }}
            />
          )}
        />
      );
    }

    if (canShouldUserAutoSync() && !isSkipCheckRouteForAutoSync()) {
      const { user } = getStore().getState();
      const autoSyncClassroomsCompleted = getLocalStorage('autoSyncClassroomsCompleted');
      const autoSyncClassroomsErrored = getLocalStorage('autoSyncClassroomsErrored');
      if (autoSyncClassroomsCompleted !== 'true' && autoSyncClassroomsErrored !== 'true') {
        return (
          <Route
            render={({ location }) => (
              <Redirect
                to={{
                  pathname: `/classroom/rostering/${user.userProfile?.providerName}/authenticatedRoute`,
                  state: { nextPathname: location.pathname },
                }}
              />
            )}
          />
        );
      }
    }

    // Takween student routing
    if (userProfile?.licenseLevel === 'TAKWEEN' && isStudent(userProfile)) {
      const grade = userProfile.grade || 0;

      // PK-5 students: Show "not available" message
      if (grade <= 5) {
        return (
          <Route
            render={() => (
              <Redirect
                to={{
                  pathname: '/takween-not-available',
                  state: { grade },
                }}
              />
            )}
          />
        );
      }
      // Grades 6-12: Continue to student portal (normal flow continues)
    }

    // Make centralized decision about whether to prompt for survey
    const shouldSkip = isSkipCheckRouteForSurvey();

    const decision = decideShouldPromptForSurvey(userProfile, {
      currentPath: location.pathname,
      decidedBy: 'AuthenticatedRoute',
    });

    // Log decision to console with full context
    console.log('[SURVEY PROMPT DECISION]', {
      shouldPrompt: decision.shouldPrompt,
      reason: decision.reason,
      decidedBy: decision.decidedBy,
      inputs: decision.telemetryData,
      shouldSkip,
      willRedirect: decision.shouldPrompt && !shouldSkip,
      redirectCount: surveyRedirectCount.current,
      lastRedirectPath: lastSurveyRedirectPath.current,
      timestamp: new Date(decision.timestamp).toISOString(),
    });

    // Log to analytics telemetry (only when backend says eligible - filters noise)
    if (userProfile && decision.telemetryData.hasBackendEligibilityFlag) {
      Analytics.surveyPromptDecision(decision, userProfile).catch((error) => {
        console.error('[SURVEY PROMPT DECISION] Analytics logging failed:', error);
      });
    }

    if (decision.shouldPrompt && !shouldSkip) {
      // Safety: Prevent infinite redirect loops
      // Track consecutive redirects from the same path
      if (lastSurveyRedirectPath.current === location.pathname) {
        // We're trying to redirect from the same path again
        surveyRedirectCount.current += 1;

        if (surveyRedirectCount.current > 3) {
          console.error('[AuthenticatedRoute] Maximum survey redirect limit reached', {
            userType: userProfile?.type,
            campaignId: userProfile?.surveyParameters?.campaignId,
            currentPath: location.pathname,
            attempts: surveyRedirectCount.current,
          });

          // Break the loop - allow navigation to intended destination
          // Reset counter so it doesn't affect future navigations
          surveyRedirectCount.current = 0;
          lastSurveyRedirectPath.current = null;
          return <Route {...rest}>{children}</Route>;
        }
      } else {
        // Different path, reset counter
        surveyRedirectCount.current = 1; // Start at 1 since we're about to redirect
        lastSurveyRedirectPath.current = location.pathname;
      }

      return (
        <Route
          render={({ location }) => (
            <Redirect
              to={{
                pathname: '/survey-choice',
                state: {
                  nextPathname: location.pathname,
                  surveyParameters: userProfile.surveyParameters,
                },
              }}
            />
          )}
        />
      );
    }

    // Not eligible or should skip - reset counter
    if (lastSurveyRedirectPath.current !== null) {
      surveyRedirectCount.current = 0;
      lastSurveyRedirectPath.current = null;
    }

    return <Route {...rest}>{children}</Route>;

    //
    // these routes should not trigger a prompt for missing
    // information, otherwise there'll be a continuous loop
    //
    function isSkipCheckRouteForAutoSync() {
      return (
        location.pathname.toLowerCase().includes('/classroom/rostering') ||
        location.pathname.toLowerCase().includes('/moreinformation/school')
      );
    }

    function isSkipCheckRouteForMissingSchool() {
      return ['/moreinformation/school'].includes(location.pathname.toLowerCase());
    }

    function isSkipCheckRouteForSurvey() {
      const path = location.pathname.toLowerCase();
      return (
        path === '/survey' ||
        path === '/survey-choice' ||
        path.includes('/classroom/rostering') ||
        path.includes('/moreinformation/school')
      );
    }

    // This function is now handled by centralized decideShouldPromptForSurvey
    // Removed to eliminate duplication
  }
};

export default AuthenticatedRoute;
